import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../hooks/hooks';

interface UseInfiniteFetchProps<TPayload extends { limit: number; }> {
    nextCursorId: number | undefined;
    nextCursorDate: number | undefined;
    loading: boolean;
    error?: string;
    limit: number;
    fetchAction: (args: TPayload) => any;
    fetchParams: Omit<TPayload, 'cursorId' | 'cursorDate' | 'limit'>;
}

export function useFetchStories<TPayload extends { limit: number }>({
                                                                                        nextCursorId,
                                                                                        nextCursorDate,
                                                                                        loading,
                                                                                        error,
                                                                                        limit,
                                                                                        fetchAction,
                                                                                        fetchParams,
                                                                                    }: UseInfiniteFetchProps<TPayload>) {
    const dispatch = useAppDispatch();
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const fetchingRef = useRef(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    useEffect(() => {
        if (!bottomRef.current) return;

        const hasMore = nextCursorId !== null && nextCursorDate !== null;

        const observer = new IntersectionObserver(entries => {
            const entry = entries[0];
            if (entry.isIntersecting && !loading && !error && hasMore && !fetchingRef.current) {
                fetchingRef.current = true;
                setIsFetchingMore(true);

                const fullParams = {
                    ...fetchParams,
                    cursorId: nextCursorId,
                    cursorDate: nextCursorDate,
                    limit,
                } as unknown as TPayload;

                dispatch(fetchAction(fullParams)).finally(() => {
                    setIsFetchingMore(false);
                    fetchingRef.current = false;
                });
            }
        }, { threshold: 0 });

        const current = bottomRef.current;
        observer.observe(current);

        return () => {
            if (current) observer.unobserve(current);
        };
    }, [
        bottomRef,
        loading,
        dispatch,
        nextCursorId,
        nextCursorDate,
        limit,
        fetchAction,
        fetchParams,
    ]);

    return { bottomRef, isFetchingMore };
}